import { memo } from 'react';
import { CardContent, CardShellEcho } from '../common';
import NoDataMessageEcho from '../../states/NoDataMessageEcho';
import { CampaignTypesChartEcho } from '@/components/charts/CampaignTypesChart/indexEcho';
import type { CampaignsByCategory, PayoutsCampaignsByCategory } from '@/types/api';
import { Button, Flex, theme } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import useCampaignTypesCardData from './useCampaignTypesCardData';
import { mixpanelTrack } from '@/utils/mixpanelUtils/asyncMixpanel';
import { MIXPANEL_EVENT, RevenueDashboardWidget } from '@/utils/mixpanelUtils/events';
import { usePageMetadata } from '@/hooks';
import CampaignTypesModalEcho from '@/components/modals/CampaignTypesModal/CampaignTypesModalEcho';

/**
 * This component is used to display the campaign types card.
 * @param className - The className for the cards shell.
 * @returns The campaign types card component.
 */
const CampaignTypesCardEcho = ({ className }: { className?: string }) => {
  const { data, isPending, isError, error } = useCampaignTypesCardData();
  const { page: pageType } = usePageMetadata();

  return (
    <CardShellEcho
      className={className}
      style={{ display: 'flex', flexDirection: 'column' }}
      testId="campaign-types-card"
      title="Campaign types"
      headerOptions={
        <CampaignTypesModalEcho
          renderTrigger={(openModal, startPrefetch) => (
            <Button
              type="text"
              color="primary"
              variant="text"
              style={{ width: 'fit-content' }}
              iconPlacement="end"
              icon={<RightOutlined />}
              onClick={() => {
                if (pageType) {
                  const event =
                    pageType === 'earnings'
                      ? MIXPANEL_EVENT.earnings.campaignTypes.moreDetailsClicked
                      : MIXPANEL_EVENT.payouts.campaignTypes.moreDetailsClicked;

                  mixpanelTrack(event, {
                    widget: RevenueDashboardWidget.CampaignTypes,
                    page: pageType,
                  });
                }

                openModal();
              }}
              onMouseEnter={startPrefetch}
            >
              More details
            </Button>
          )}
        />
      }
    >
      <CardContent
        isPending={isPending}
        isError={isError}
        error={error}
        data={data}
        renderData={(campaignsData) => <Data data={campaignsData} />}
      />
    </CardShellEcho>
  );
};

interface DataProps {
  data: CampaignsByCategory | PayoutsCampaignsByCategory;
}

const Data = memo(({ data }: DataProps) => {
  const { token } = theme.useToken();
  const campaignTypeItems = Object.entries(data);

  if (campaignTypeItems?.length === 0) {
    return <NoDataMessageEcho dataType="campaigns" />;
  }

  //TODO: open all category types in detail view
  return (
    <Flex
      vertical
      justify="center"
      style={{ width: '100%', gap: token.marginMD, marginTop: token.marginMD }}
    >
      <CampaignTypesChartEcho campaignTypes={data as CampaignsByCategory} />
    </Flex>
  );
});

export default CampaignTypesCardEcho;
